import { Controller } from "@hotwired/stimulus"
import { loadMapEngine, getPixelRatio, setupDprResize, LOCAL_CJK_FONT } from "./map_engine"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { showError } from "../lib/platform_toast"

// Connects to data-controller="map-picker"
// Self-hosted MapLibre GL JS + PMTiles location picker (the only engine — no Google API/key).
// Public methods (updateMarker/updateRadius) + CustomEvent contracts let callers (ad_wizard,
// product-form, listing-wizard, rescue-animal-form) stay engine-agnostic.
const log = createLogger('MapPicker')

export default class extends Controller {
  static targets = ["container", "latitude", "longitude"]
  static values = {
    styleUrl: String,               // self-hosted style.json
    radius: { type: Number, default: 3.0 }, // Radius in kilometers (matches BusinessConstants::AD_RADIUS_DEFAULT_KM)
    showCircle: { type: Boolean, default: true }, // Show radius circle (default: true for backward compatibility)
    zoom: { type: Number, default: 15 } // Map zoom level (1-20: 1=world, 10=city, 15=streets, 20=buildings)
  }

  connect() {
    // Setup boundary error listener (store bound reference for proper cleanup)
    this.boundHandleBoundaryError = this.handleBoundaryError.bind(this)
    this.element.addEventListener('map-picker:boundary-error', this.boundHandleBoundaryError)

    this.initSelfHostedMap()       // self-hosted MapLibre + PMTiles (no Google API)
  }

  disconnect() {
    // Clean up event listener when controller is disconnected
    this.element.removeEventListener('map-picker:boundary-error', this.boundHandleBoundaryError)
    // Free the WebGL context on teardown — browsers cap live contexts, so a map recreated on every
    // step/navigation leaks them unless released here. map.remove() THROWS when the controller
    // disconnects before MapLibre finished initializing (leaving a step before the style/painter are
    // ready) — it reaches into internals that don't exist yet. Guard it, and on that init-race drop the
    // WebGL context directly instead of leaking it.
    this._teardownDpr?.()
    try {
      this.marker?.remove?.()
      this.map?.remove?.()
    } catch (e) {
      log.warn('MapLibre teardown during init race; releasing WebGL context directly', e)
      try {
        const canvas = this.map?.getCanvas?.()
        const gl = canvas?.getContext?.('webgl2') || canvas?.getContext?.('webgl')
        gl?.getExtension?.('WEBGL_lose_context')?.loseContext?.()
      } catch (_) { /* best-effort; GC reclaims the context */ }
    }
    this.map = null
    this.marker = null
    this.engine = null
    // Reset the init guard. Under Turbo morph (turbo-refresh-method: morph), Stimulus REUSES this same
    // controller instance across step navigation (disconnect → connect on the same object, not a fresh
    // one), so a sticky isInitialized would permanently block re-init after the first teardown and the map
    // would never come back when returning to step 3. Resetting here makes the next connect() rebuild it.
    this.isInitialized = false
  }

  // Public method: Update map when coordinates change externally (e.g., "현재 위치" button, autocomplete)
  updateMarker(lat, lng, address = null) {
    log.debug('updateMarker called', { lat, lng, address })

    if (!this.map || !this.marker) {
      log.warn('Map not initialized yet, cannot update marker', {
        hasMap: !!this.map,
        hasMarker: !!this.marker
      })
      return
    }

    const latitude = parseFloat(lat)
    const longitude = parseFloat(lng)

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      log.error('Invalid coordinates', { lat, lng })
      return
    }

    // MapLibre uses [lng, lat] arrays everywhere (GeoJSON order)
    this.marker.setLngLat([longitude, latitude])
    this.updateCircle(longitude, latitude)
    this.map.panTo([longitude, latitude])

    const latString = latitude.toFixed(6)
    const lngString = longitude.toFixed(6)
    this.latitudeTarget.value = latString
    this.longitudeTarget.value = lngString

    // Include address in event if provided (e.g., from autocomplete)
    // This prevents handleMapCoordinateChange() from overwriting the address with coordinates
    this.element.dispatchEvent(new CustomEvent("map-picker:coordinate-changed", {
      detail: {
        latitude: latString,
        longitude: lngString,
        address: address // Include address to preserve it
      },
      bubbles: true
    }))

    log.debug('Map marker updated successfully', {
      latitude: latString,
      longitude: lngString,
      address
    })
  }

  // Public method: Update radius circle when slider changes
  updateRadius(km) {
    log.debug('updateRadius called', { km })

    const radiusKm = parseFloat(km)
    if (Number.isNaN(radiusKm) || radiusKm <= 0) {
      log.error('Invalid radius value', { km })
      return
    }
    this.radiusValue = radiusKm

    // Null-guard: ad_wizard calls updateRadius without checking readiness, and the engine
    // loads async — bail until the marker exists (then updateCircle reflects the new radius).
    if (!this.map || !this.marker) {
      log.warn('Map not initialized yet, cannot update radius')
      return
    }
    const { lng, lat } = this.marker.getLngLat()
    this.updateCircle(lng, lat)

    log.debug('Circle radius updated successfully', { km: radiusKm })
  }

  // Debounce utility function to limit API calls
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Helper: Check if coordinates are within Taiwan boundaries
  // Uses BusinessConstants.GPS for SSOT (server-side: BusinessConstants::GPS::TAIWAN_*)
  isWithinTaiwan(lat, lng) {
    const { TAIWAN_MIN_LAT, TAIWAN_MAX_LAT, TAIWAN_MIN_LNG, TAIWAN_MAX_LNG } = window.BusinessConstants.GPS

    return lat >= TAIWAN_MIN_LAT && lat <= TAIWAN_MAX_LAT &&
           lng >= TAIWAN_MIN_LNG && lng <= TAIWAN_MAX_LNG
  }

  // Boundary-error toast text via i18n (data-i18n-boundary-error on the mount); the CJK literal is a
  // single last-resort fallback, matching the markerTitle convention — no raw user-facing strings inline.
  get boundaryErrorMessage() {
    return getI18nMessage(this.element, 'boundaryError', '廣告目標區域必須在台灣境內')
  }

  // Taiwan boundary error: the shared toast (sits above the sticky wizard header, announced, no HTML injection)
  handleBoundaryError(event) {
    showError(event.detail.message || this.boundaryErrorMessage)
  }

  // ============================================================================
  // Self-hosted MapLibre GL JS + PMTiles map.
  // ============================================================================

  async initSelfHostedMap() {
    if (this.isInitialized) return
    this.isInitialized = true

    try {
      // Go through the shared adapter so the pmtiles protocol is registered exactly once
      // (the region map uses the same maplibregl module instance).
      this.engine = await loadMapEngine()
    } catch (e) {
      log.error('Failed to load self-hosted map engine', e)
      return
    }
    if (!this.element.isConnected) return

    const lat = parseFloat(this.latitudeTarget.value) || 25.033   // Taipei City Hall fallback
    const lng = parseFloat(this.longitudeTarget.value) || 121.565

    this.map = new this.engine.mapClass({
      container: this.containerTarget,
      style: this.styleUrlValue,
      center: [ lng, lat ],                          // MapLibre = [lng, lat]
      zoom: this.zoomValue,
      maxBounds: [ [ 117.5, 21.0 ], [ 123.0, 26.5 ] ],
      attributionControl: false,                        // no on-map watermark — OSM/ODbL credits live in
                                                        // Terms/Privacy (terms.yml §地圖與行情資料), same as
                                                        // the static-map thumbnail. Legal attribution preserved.
      pixelRatio: getPixelRatio(),                      // capped hi-DPR backing store (shared via map_engine; was unset → uncapped)
      // CJK 繁體中文 via on-device TinySDF 48px (sharper than the 24px server SDF); Latin stays server-SDF.
      localIdeographFontFamily: LOCAL_CJK_FONT
      // One-finger drag pans the map naturally — MapLibre puts touch-action:none on its canvas
      // (.maplibregl-touch-drag-pan), so the PAGE does NOT scroll over the map (swipe ABOVE/BELOW the
      // small map to scroll the form). The marker carries its own touch-action lock (below). We do NOT
      // use cooperativeGestures: it forced two-finger pan, which read as "the map won't move".
    })
    this.map.addControl(new this.engine.NavigationControl({ showCompass: false }), "top-right")
    // Re-apply pixelRatio on DPR change (page-zoom / display move / devtools device-mode) so the picker
    // canvas doesn't go stale — region_map already did this; map_picker did not (shared helper, map_engine).
    this._teardownDpr = setupDprResize(this.map)

    const markerTitle = getI18nMessage(this.element, 'markerTitle', '拖曳標記以調整位置')
    this.marker = new this.engine.Marker({ draggable: true }).setLngLat([ lng, lat ]).addTo(this.map)
    const markerEl = this.marker.getElement()
    markerEl.setAttribute('title', markerTitle)
    // The pin is a DOM element OUTSIDE MapLibre's gesture layer — without touch-action:none the browser
    // claims a vertical drag-on-the-pin as a page SCROLL (cooperativeGestures only covers map pan/zoom,
    // not the marker). Lock it so dragging the pin moves the pin, not the page.
    markerEl.style.touchAction = "none"
    markerEl.style.cursor = "grab"

    if (this.showCircleValue) {
      this.map.on('load', () => {
        this.map.addSource('picker-radius', { type: 'geojson', data: this.circlePolygon(lng, lat, this.radiusValue) })
        this.map.addLayer({ id: 'picker-radius-fill', type: 'fill', source: 'picker-radius',
          paint: { 'fill-color': '#FF9E45', 'fill-opacity': 0.3 } })
        this.map.addLayer({ id: 'picker-radius-line', type: 'line', source: 'picker-radius',
          paint: { 'line-color': '#FF9E45', 'line-width': 2 } })
      })
    }

    this.debouncedGeocode = this.debounce(async (lng2, lat2) => {
      try {
        const res = await fetch(`/api/v1/reverse_geocode?lat=${lat2}&lng=${lng2}`, { headers: { Accept: 'application/json' } })
        if (!res.ok) return
        const data = (await res.json())?.data
        const address = data?.address
        // Only dispatch when we actually resolved an address — never overwrite a good value with null.
        // Carry the precision tier ("house"/"street"/"district") + distance so the form can show an honest
        // accuracy indicator instead of silently filling a district-only string.
        if (address) {
          this.element.dispatchEvent(new CustomEvent("map-picker:address-resolved", {
            detail: {
              latitude: lat2.toFixed(6), longitude: lng2.toFixed(6),
              address, precision: data?.precision, distanceM: data?.distance_m
            },
            bubbles: true
          }))
        }
      } catch (e) { log.error('Reverse geocode error', e) }
    }, window.BusinessConstants.UITiming.GEOCODING_DEBOUNCE_MS)

    // Two ways to set the location: DRAG the pin, or simply TAP anywhere on the map (far easier than
    // dragging a small pin on a phone). Both funnel through commitMarkerPosition.
    this.marker.on('dragend', () => {
      const { lng, lat } = this.marker.getLngLat()
      this.commitMarkerPosition(lng, lat)
    })
    this.map.on('click', (e) => this.commitMarkerPosition(e.lngLat.lng, e.lngLat.lat))
  }

  // Snap the pin to (lng, lat) from a drag OR a tap, validate Taiwan bounds (reset + toast if outside),
  // persist the coords, and reverse-geocode the address. Shared by marker dragend + map click so the
  // address ALWAYS refreshes when the location changes — by drag or by tap.
  commitMarkerPosition(lng, lat) {
    if (!this.isWithinTaiwan(lat, lng)) {
      this.marker.setLngLat([ 121.565, 25.033 ])
      this.updateCircle(121.565, 25.033)
      this.element.dispatchEvent(new CustomEvent("map-picker:boundary-error",
        { detail: { message: this.boundaryErrorMessage }, bubbles: true }))
      return
    }
    this.marker.setLngLat([ lng, lat ])
    const latitude = lat.toFixed(6), longitude = lng.toFixed(6)
    this.latitudeTarget.value = latitude
    this.longitudeTarget.value = longitude
    this.updateCircle(lng, lat)
    this.element.dispatchEvent(new CustomEvent("map-picker:coordinate-changed",
      { detail: { latitude, longitude }, bubbles: true }))
    this.debouncedGeocode(lng, lat)
  }

  // GeoJSON circle polygon (no turf dependency). Equirectangular meters→degrees approximation,
  // <0.1% error at Taiwan latitudes / ≤10km radii. Matches house style (region_map hand-rolls GeoJSON).
  circlePolygon(lng, lat, radiusKm, steps = 64) {
    const coords = []
    const dx = radiusKm / (111.320 * Math.cos(lat * Math.PI / 180)) // deg lng per km
    const dy = radiusKm / 110.574                                   // deg lat per km
    for (let i = 0; i < steps; i++) {
      const t = (i / steps) * 2 * Math.PI
      coords.push([ lng + dx * Math.cos(t), lat + dy * Math.sin(t) ])
    }
    coords.push(coords[0]) // close ring
    return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ coords ] } }
  }

  updateCircle(lng, lat) {
    const src = this.map?.getSource?.('picker-radius') // undefined before style 'load'
    if (src) src.setData(this.circlePolygon(lng, lat, this.radiusValue))
  }
}
